import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BaseComponent } from '@core/components/base-component';
import { TargetedEvent } from '@core/helpers/events/targeted-event';
import { SortHelper } from '@core/helpers/sort.helper';
import { StatusEnum } from '@enums/Status';
import { StructureDepthEnum } from '@enums/StructureDepth';
import { DepartmentEstimate } from '@interfaces/DepartmentEstimate';
import { Estimate } from '@interfaces/Estimate';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { Level1 } from '@interfaces/Level1';
import { Level2 } from '@interfaces/Level2';
import { ProblematicSubLevelsDto } from '@interfaces/ProblematicSubLevelsDto';
import { Structure } from '@interfaces/Structure';
import { TranslateService } from '@ngx-translate/core';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateStructureLinksService } from '@shared/services/estimate-structure-links.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LoadingService } from '@shared/services/loading.service';
import { Flags } from '@shared/services/models/flags.enum';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { EMPTY, Observable, combineLatest, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'pop-structure-modal',
  templateUrl: 'structure-modal.component.html',
  styleUrls: ['structure-modal.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class StructureModalComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly level1: string = 'level1';
  private readonly level2: string = 'level2';
  public Flags = Flags;

  public copiedNode: TreeNode<Level1 | Level2>;
  public currentEstimate: Estimate;
  private currentNode: TreeNode<Level1 | Level2>;
  private departmentEstimates: DepartmentEstimate[];
  private duplicateCode = false;
  private editableNode: TreeNode<Level1 | Level2>;
  public expandedLevel1Ids: string[] = <string[]>[];
  private expandedLevel2Ids: string[] = <string[]>[];
  public isExpandHidden = false;
  private isGroupApproved: boolean;
  public isLevel2: boolean;
  public isReadOnly: boolean;
  public level1Tree: TreeNode<Level1 | Level2>[];
  public problematicSublevels: ProblematicSubLevelsDto;
  private settings: EstimateConfiguration;
  private showLevel1Node = false;
  private showLevel2Node = false;
  private structures: Structure[];
  public projectTemplates: string[];

  @ViewChild('code') public codeInput: ElementRef;
  @ViewChild('newNodeLevel2') public newNodeLevel2: ElementRef<HTMLDivElement>;
  @ViewChild('newNodeLevel1') public newNodeLevel1: ElementRef<HTMLDivElement>;
  @ViewChild('level2Code') public level2Code: ElementRef;

  private _selectedNode: TreeNode<Level1 | Level2> = null;
  public get selectedNode(): TreeNode<Level1 | Level2> | null {
    return this._selectedNode;
  }
  private set selectedNode(value: TreeNode<Level1 | Level2>) {
    this._selectedNode = value;
  }

  public constructor(
    private estimateService: EstimateService,
    private estimateSettings: EstimateSettingsService,
    private confirmationService: ConfirmationService,
    private loadingService: LoadingService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private estimateStructureLinksService: EstimateStructureLinksService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    super();

    this.structures = <Structure[]>[];
    this.settings = <EstimateConfiguration>{};
    this.departmentEstimates = <DepartmentEstimate[]>[];
    this.level1Tree = <TreeNode<Level1 | Level2>[]>[];
  }

  public async ngOnInit() {
    this.loadingService.setIsLoading(true);

    this.subscribe(
      combineLatest([
        this.estimateService.observeCurrentEstimate(),
        this.estimateService.observeIsCurrentEstimateGroupApproved(),
        this.estimateService.observeAlreadyDeployedSubLevels()
      ]).subscribe(([estimate, isGroupApproved, problematicSublevels]) => {
        this.currentEstimate = estimate;
        this.problematicSublevels = problematicSublevels;
        this.isGroupApproved = isGroupApproved;
        this.departmentEstimates = SortHelper.sortArrayByProperty(
          this.currentEstimate.departmentEstimates,
          o => o.department?.code
        ).slice();

        // create structure tree
        this.createTree();
        this.getLevel();

        this.loadingService.setIsLoading(false);
        this.isReadOnly = this.currentEstimate.status !== StatusEnum.New || this.isGroupApproved;
      })
    );
    this.subscribe(
      this.estimateSettings.getEstimateConfiguration(false).subscribe(results => {
        this.settings = results;
      })
    );

    this.onDocumentClick = this.onDocumentClick.bind(this);
    document.addEventListener('click', event => this.onDocumentClick(event));

    const templates = await this.estimateService.getProjectTemplatesAsync();
    this.projectTemplates = templates.map(t => t.code);

    this.detectModalShouldBeOpen();
    this.subscribe(
      this.router.events.subscribe(routerEvent => {
        if (routerEvent instanceof NavigationEnd) {
          this.detectModalShouldBeOpen();
        }
      })
    );
  }

  public modalIsOpen = false;
  public async closeModal() {
    await this.router.navigate(['.'], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge' // remove to replace all query params by provided
    });
  }

  private detectModalShouldBeOpen() {
    this.modalIsOpen = this.activatedRoute.children.length > 0;
  }

  public getIsMaxBudgetedForToggle(
    level1Node: TreeNode<Level1 | Level2>,
    level2Node?: TreeNode<Level1 | Level2>
  ): boolean {
    let isMaxBudgeted = false;

    if (level2Node) {
      const structures = this.currentEstimate.structures.filter(
        s => s.level1Id === level1Node.data.id && s.level2Id === level2Node.data.id
      );
      if (structures.length > 0) {
        const structure = structures[0];
        isMaxBudgeted = structure.level2IsMaxBudgeted;
      }
    } else {
      const structures = this.currentEstimate.structures.filter(s => s.level1Id === level1Node.data.id);
      let level1IsMaxBudgeted = true;
      for (const structure of structures) {
        if (!structure.level1IsMaxBudgeted) {
          level1IsMaxBudgeted = false;
          break;
        }
      }
      isMaxBudgeted = level1IsMaxBudgeted;
    }
    return isMaxBudgeted;
  }

  public changeMaxBudgetedToggle(
    input: HTMLInputElement,
    applyToAll: boolean,
    level1Node: TreeNode<Level1 | Level2>,
    level2Node?: TreeNode<Level1 | Level2>
  ) {
    const isMaxBudgeted: boolean = input.checked;
    if (level2Node) {
      const structures = this.currentEstimate.structures.filter(
        s => s.level1Id === level1Node.data.id && s.level2Id === level2Node.data.id
      );
      if (structures.length > 0) {
        const structure = structures[0];
        structure.level2IsMaxBudgeted = isMaxBudgeted;
      }
    } else {
      const structures = this.currentEstimate.structures.filter(s => s.level1Id === level1Node.data.id);
      for (const structure of structures) {
        structure.level1IsMaxBudgeted = isMaxBudgeted;
        if (applyToAll) {
          structure.level2IsMaxBudgeted = isMaxBudgeted;
        }
      }
    }
  }

  public getIsLumpSumForToggle(level1Node: TreeNode<Level1 | Level2>, level2Node?: TreeNode<Level1 | Level2>): boolean {
    let isLumpSum = false;

    if (level2Node) {
      const structures = this.currentEstimate.structures.filter(
        s => s.level1Id === level1Node.data.id && s.level2Id === level2Node.data.id
      );
      if (structures.length > 0) {
        const structure = structures[0];
        isLumpSum = structure.level2IsLumpSum;
      }
    } else {
      const structures = this.currentEstimate.structures.filter(s => s.level1Id === level1Node.data.id);
      let level1IsLumpSum = true;
      for (const structure of structures) {
        if (!structure.level1IsLumpSum) {
          level1IsLumpSum = false;
          break;
        }
      }
      isLumpSum = level1IsLumpSum;
    }
    return isLumpSum;
  }

  public changeIsLumpSumToggle(
    input: HTMLInputElement,
    applyToAll: boolean,
    level1Node: TreeNode<Level1 | Level2>,
    level2Node?: TreeNode<Level1 | Level2>
  ) {
    const isLumpSum: boolean = input.checked;

    if (level2Node) {
      const structure = this.currentEstimate.structures.find(
        s => s.level1Id === level1Node.data.id && s.level2Id === level2Node.data.id
      );
      if (structure) {
        structure.level2IsLumpSum = isLumpSum;
      }
    } else {
      const structures = this.currentEstimate.structures.filter(s => s.level1Id === level1Node.data.id);
      for (const structure of structures) {
        structure.level1IsLumpSum = isLumpSum;
        if (applyToAll) {
          structure.level2IsLumpSum = isLumpSum;
        }
      }
    }
  }

  public getIsUsingLaborBillingMethodForToggle(
    level1Node: TreeNode<Level1 | Level2>,
    level2Node?: TreeNode<Level1 | Level2>
  ): boolean {
    let isUsingLaborBillingMethod = false;

    if (level2Node) {
      const structures = this.currentEstimate.structures.filter(
        s => s.level1Id === level1Node.data.id && s.level2Id === level2Node.data.id
      );
      if (structures.length > 0) {
        const structure = structures[0];
        isUsingLaborBillingMethod = structure.level2IsUsingLaborBillingMethod;
      }
    } else {
      const structures = this.currentEstimate.structures.filter(s => s.level1Id === level1Node.data.id);
      let level1IsUsingLaborBillingMethod = true;
      for (const structure of structures) {
        if (!structure.level1IsUsingLaborBillingMethod) {
          level1IsUsingLaborBillingMethod = false;
          break;
        }
      }
      isUsingLaborBillingMethod = level1IsUsingLaborBillingMethod;
    }
    return isUsingLaborBillingMethod;
  }

  public changeIsUsingLaborBillingMethodToggle(
    event: Event,
    applyToAll: boolean,
    level1Node: TreeNode<Level1 | Level2>,
    level2Node?: TreeNode<Level1 | Level2>
  ) {
    const targetedEvent = event as TargetedEvent<HTMLInputElement>;
    const isUsingLaborBillingMethod: boolean = targetedEvent.target.checked;

    if (level2Node) {
      const structures = this.currentEstimate.structures.filter(
        s => s.level1Id === level1Node.data.id && s.level2Id === level2Node.data.id
      );
      if (structures.length > 0) {
        const structure = structures[0];
        structure.level2IsUsingLaborBillingMethod = isUsingLaborBillingMethod;
      }
    } else {
      const structures = this.currentEstimate.structures.filter(s => s.level1Id === level1Node.data.id);
      for (const structure of structures) {
        structure.level1IsUsingLaborBillingMethod = isUsingLaborBillingMethod;
        if (applyToAll) {
          structure.level2IsUsingLaborBillingMethod = isUsingLaborBillingMethod;
        }
      }
    }
    this.estimateService.saveChanges().subscribe();
  }

  private onDocumentClick(e: Event) {
    const event = e as TargetedEvent;
    if (this.showLevel1Node) {
      if (!this.newNodeLevel1?.nativeElement.contains(event.target)) {
        this.updateNode(this.currentNode);
        return (this.showLevel1Node = false);
      }
    }
    if (this.showLevel2Node) {
      if (!this.newNodeLevel2?.nativeElement.contains(event.target)) {
        this.updateNode(this.currentNode);
        return (this.showLevel2Node = false);
      }
    }
  }

  public ngOnDestroy() {
    document.removeEventListener('click', event => this.onDocumentClick(event));
  }

  public async openImportDialog() {
    await this.loadingService.run(async () => {
      await this.estimateService.saveChangesAsync();

      await this.router.navigate(['import'], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge' // remove to replace all query params by provided
      });
    });
  }

  public confirmUsingLaborBillingMethod(event: Event, level1Node: TreeNode<Level1 | Level2>) {
    this.confirmationService.confirm({
      message: this.translate.instant('ERROR.APPLY_ALL') as string,
      header: this.translate.instant('"STRUCTURE_MODAL.LABOR_BILLING_METHOD"') as string,
      icon: 'fas fa-exclamation-triangle',
      key: 'global-confirmation-dialog',
      acceptLabel: this.translate.instant('BUTTON.YES') as string,
      rejectLabel: this.translate.instant('BUTTON.NO') as string,
      accept: () => {
        this.changeIsUsingLaborBillingMethodToggle(event as TargetedEvent<HTMLInputElement>, true, level1Node);
      },
      reject: () => {
        this.changeIsUsingLaborBillingMethodToggle(event as TargetedEvent<HTMLInputElement>, false, level1Node);
      }
    });
  }

  public confirmLumpSum(
    event: HTMLInputElement,
    level1Node: TreeNode<Level1 | Level2>,
    level2Node?: TreeNode<Level1 | Level2>
  ) {
    if (!level2Node) {
      this.confirmationService.confirm({
        message: this.translate.instant('ERROR.APPLY_ALL') as string,
        header: this.translate.instant('HEADER.METHOD') as string,
        icon: 'fas fa-exclamation-triangle',
        key: 'global-confirmation-dialog',
        acceptLabel: this.translate.instant('BUTTON.YES') as string,
        rejectLabel: this.translate.instant('BUTTON.NO') as string,
        accept: () => {
          this.changeIsLumpSumToggle(event, true, level1Node);
        },
        reject: () => {
          this.changeIsLumpSumToggle(event, false, level1Node);
        }
      });
    }
  }

  public confirmMaxBudgeted(
    input: HTMLInputElement,
    level1Node: TreeNode<Level1 | Level2>,
    level2Node?: TreeNode<Level1 | Level2>
  ) {
    if (!level2Node) {
      this.confirmationService.confirm({
        message: this.translate.instant('ERROR.APPLY_ALL') as string,
        header: this.translate.instant('HEADER.MAX_BUDGETED') as string,
        icon: 'fas fa-exclamation-triangle',
        key: 'global-confirmation-dialog',
        acceptLabel: this.translate.instant('BUTTON.YES') as string,
        rejectLabel: this.translate.instant('BUTTON.NO') as string,
        accept: () => {
          this.changeMaxBudgetedToggle(input, true, level1Node);
        },
        reject: () => {
          this.changeMaxBudgetedToggle(input, false, level1Node);
        }
      });
    }
  }

  public confirmDelete() {
    this.confirmationService.confirm({
      message: this.translate.instant('ERROR.NODE_DELETE') as string,
      header: this.translate.instant('HEADER.REMOVE_NODE') as string,
      icon: 'fas fa-exclamation-triangle',
      key: 'global-confirmation-dialog',
      acceptLabel: this.translate.instant('BUTTON.CONFIRM') as string,
      rejectLabel: this.translate.instant('BUTTON.CANCEL') as string,
      accept: () => {
        this.removeSubLevel();
        this.nodeUnselect();
      },
      reject: () => {}
    });
  }

  /**
   * TODO : optimise tree creation, currently many loops involved
   * */
  private createTree() {
    const structures = SortHelper.sortStructures(this.currentEstimate.structures);

    this.level1Tree = <TreeNode<Level1 | Level2>[]>[];
    for (const struct of structures) {
      if (struct.level1) {
        let level1NodeExists = false;
        for (const level1Node of this.level1Tree) {
          // if a level1Node already exists for this level1

          if (struct.level1.code === level1Node.data.code) {
            level1NodeExists = true;
            this.addLevel1Node(struct, level1Node);
            break;
          }
        }
        if (!level1NodeExists) this.addLevel1Node(struct);
      }
    }

    // expand nodes by default
    for (const node of this.level1Tree) {
      this.expandNode(node);
      for (const child of node.children) {
        if (child.type === 'level2') {
          this.expandNode(child);
        }
      }
    }
  }

  public hasDuplicate(event: any, node: TreeNode<Level1 | Level2>) {
    if (node.type === this.level1) {
      for (const struct of this.currentEstimate.structures) {
        if (struct.level1.code === event) {
          this.duplicateCode = true;
          return this.duplicateCode;
        }
      }
    } else {
      for (const level2Node of node.parent.children) {
        if (level2Node.data.code === event) {
          this.duplicateCode = true;
          return this.duplicateCode;
        }
      }
    }

    node.data.code = event;
    this.duplicateCode = false;
    return this.duplicateCode;
  }

  private getLevel() {
    if (this.currentEstimate.structureDepth === StructureDepthEnum.Level1) {
      this.isLevel2 = false;
    } else if (this.currentEstimate.structureDepth === StructureDepthEnum.Level2) {
      this.isLevel2 = true;
    }
  }

  private addLevel1Node(structure: Structure, level1Node?: TreeNode<Level1 | Level2>) {
    let newLevel1Node: TreeNode<Level1 | Level2>;

    // if no level1Node given, create new Level 1 Node
    if (level1Node) {
      newLevel1Node = level1Node;
    } else {
      newLevel1Node = this.createNode(
        structure.level1.code,
        structure.level1.description,
        structure.level1,
        this.level1
      );
    }

    // add Departments or Level2s to Level1
    if (this.currentEstimate.structureDepth === StructureDepthEnum.Level2) {
      if (structure.level2) {
        newLevel1Node = this.addLevel2Node(newLevel1Node, structure);
      }
    }

    // update level1Tree
    if (level1Node) {
      level1Node = newLevel1Node;
    } else {
      this.level1Tree.push(newLevel1Node);
    }

    return newLevel1Node;
  }

  private addLevel2Node(parentNode: TreeNode<Level1 | Level2>, structure: Structure) {
    // create level2 node
    const level2Node = this.createNode(
      structure.level2.code,
      structure.level2.description,
      structure.level2,
      this.level2,
      parentNode
    );

    // add to level1 node
    parentNode.children.push(level2Node);
    return parentNode;
  }

  private createNode(
    code: string,
    name: string,
    data: Level1 | Level2,
    type: string,
    parent?: TreeNode<Level1 | Level2>
  ): TreeNode<Level1 | Level2> {
    const treeNode = <TreeNode<Level1 | Level2>>{};
    treeNode.data = data;
    code = code ?? '---';
    name = name ?? '---';
    treeNode.label = code + ' \u2022 ' + name;
    treeNode.children = <TreeNode<Level1 | Level2>[]>[];
    treeNode.parent = parent;
    treeNode.type = type;
    return treeNode;
  }

  public updateNode(node: TreeNode<Level1 | Level2>) {
    const code = node.data.code ?? '---';
    const desc = node.data.description ?? '---';
    node.data.code = code.toUpperCase();
    node.data.description = desc;
    node.label = code + ' \u2022 ' + desc;
    this.expandNode(node);
    this.editableNode = null;
    this.estimateService.saveChanges().subscribe();
  }

  public editSubLevel(node: TreeNode<Level1 | Level2>) {
    if (node.type === 'level1') {
      this.showLevel1Node = true;
      this.currentNode = node;
      return (this.editableNode = node);
    } else if (node.type === 'level2') {
      this.showLevel2Node = true;
      this.currentNode = node;
      return (this.editableNode = node);
    }
  }

  public copyNode(selectedNode: TreeNode<Level1 | Level2>) {
    this.copiedNode = selectedNode;
  }

  public duplicateNode(selectedNode: TreeNode<Level1 | Level2>) {
    this.copyNode(selectedNode);
    this.pasteNode(this.copiedNode);
  }

  public pasteNode(copiedNode: TreeNode<Level1 | Level2>) {
    let obs: Observable<string | string[]>;

    if (copiedNode.type === this.level1) {
      const level1 = copiedNode.data as Level1;
      obs = this.estimateService.copyLevel1Structures(level1.id);
    } else {
      const level1 = copiedNode.parent.data as Level1;
      const level2 = copiedNode.data as Level2;
      if (this.selectedNode) {
        const targetLevel1: Level1 =
          this.selectedNode.type === this.level1
            ? (this.selectedNode.data as Level1)
            : (this.selectedNode.parent.data as Level1);
        obs = this.estimateService.copyLevel2Structures(level1.id, level2.id, targetLevel1.id);
      }
    }

    obs
      .pipe(
        switchMap(() => {
          return this.estimateService.refreshEstimate();
        })
      )
      .subscribe(estimate => {
        this.currentEstimate = estimate;
        this.departmentEstimates = SortHelper.sortArrayByProperty(
          this.currentEstimate.departmentEstimates,
          o => o.department?.code
        );
        // create structure tree
        this.createTree();
        this.getLevel();
      });
  }

  public showInputs(node: TreeNode<Level1 | Level2>): boolean {
    return this.editableNode === node;
  }

  public showSelected(node: TreeNode<Level1 | Level2>): boolean {
    return this._selectedNode === node;
  }

  public nodeSelect(node: TreeNode<Level1 | Level2>) {
    this.selectedNode = node;
  }

  public ignoreLevelWarning(node: TreeNode<Level1 | Level2>) {
    node.data.isNew = false;
  }

  private nodeUnselect() {
    this.selectedNode = null;
    this.showLevel1Node = false;
    this.showLevel2Node = false;
  }

  /**
   * Used to determine the value of a new sublevel's UseLaborBillingMethod property
   * Rules :
   * If the company forces their billing method setting, return the company setting value;
   *  Else return the estimate's default value
   * @param sublevelIsLumpSum
   */
  private getSublevelUseLaborBillingMethod() {
    if (this.settings.forceLaborBillingMethod) {
      return this.settings.useLaborBillingMethod;
    }

    return this.currentEstimate.useLaborBillingMethod;
  }

  /**
   * Used to determine the value of a new sublevel's UseMaxBudgeted property
   * Rules :
   * If the company forces their max budgeted setting, return the company setting value;
   * Else
   *  If the sublevel is lump sum, it cannot be max budgeted (return false);
   *  Else return the estimate's default value
   * @param sublevelIsLumpSum
   */
  private getSublevelUseMaxBudgeted() {
    if (this.settings.forceMaxBudgeted) return this.settings.useMaxBudgeted;

    return this.currentEstimate.maxBudgeted;
  }

  public isLaborBillingMethodToggleDisabled(sublevelIsLumpSum: boolean) {
    return (
      this.isReadOnly ||
      this.settings.forceLaborBillingMethod ||
      (!this.settings.useLaborBillingMethodWhenLumpSum && sublevelIsLumpSum)
    );
  }

  public isLumpSumToggleDisabled(useMaxBudgeted: boolean, useLaborBillingMethod: boolean) {
    return (
      this.isReadOnly || useMaxBudgeted || (!this.settings.useLaborBillingMethodWhenLumpSum && useLaborBillingMethod)
    );
  }

  public isMaxBudgetedToggleDisabled(sublevelIsLumpSum: boolean) {
    return this.isReadOnly || this.settings.forceMaxBudgeted || sublevelIsLumpSum;
  }

  public addLevel1() {
    // create new Level1 entity
    const newLevel1 = this.estimateService.createLevel1({});
    // create new Structure entity
    const level1IsMaxBudgeted = this.getSublevelUseMaxBudgeted();
    const level1IsUsingLaborBillingMethod = this.getSublevelUseLaborBillingMethod();

    this.estimateService.createStructure(
      this.currentEstimate.id,
      newLevel1.id,
      '',
      '',
      level1IsMaxBudgeted,
      false,
      level1IsUsingLaborBillingMethod
    );
    // create new Level1 Node
    const newLevel1Node = this.createNode(newLevel1.code, newLevel1.description, newLevel1, this.level1);
    // if currentEstimate not using Level2s -> create Department Nodes and add to Level1 Node
    if (this.currentEstimate.structureDepth === StructureDepthEnum.Level1) {
      for (const departmentEstimate of this.departmentEstimates) {
        if (departmentEstimate.isAssociatedToAllStructures) {
          this.addDept(departmentEstimate, newLevel1Node);
        }
      }
    }
    // add Level1 Node to Tree
    this.level1Tree.push(newLevel1Node);
    this.editSubLevel(newLevel1Node);
  }

  public addLevel2() {
    let level1Node: TreeNode<Level1 | Level2>;
    //set level1Node (parent)
    if (this.selectedNode?.type === this.level1) level1Node = this.selectedNode;
    else if (this.selectedNode?.type === this.level2) level1Node = this.selectedNode.parent;
    else return;

    // create new Level2 entity
    const newLevel2 = this.estimateService.createLevel2({});
    // add Level2 entity to related Structure
    const level2IsMaxBudgeted = this.getSublevelUseMaxBudgeted();
    const level2IsUsingLaborBillingMethod = this.getSublevelUseLaborBillingMethod();
    this.estimateService
      .findLevel2Structure(this.currentEstimate.id, level1Node.data.id, null)
      .pipe(
        switchMap(result => {
          let structure: Structure = null;
          if (result) {
            // there is a structure with an empty level2 (ex. level1 with no level2)
            structure = result;
            structure.level2Id = newLevel2.id;
            structure.level2IsMaxBudgeted = level2IsMaxBudgeted;
            structure.level2IsUsingLaborBillingMethod = level2IsUsingLaborBillingMethod;
          } else {
            // there are no structures with empty level2s (ex. level1 already has level2s), we need to create a new structure
            // go get the flag values for the parent (level1)
            const parentStructure = this.structures.find(s => s.level1Id === level1Node.data.id);
            const level1IsLumpSum = parentStructure?.level1IsLumpSum ?? false;
            const level1IsMaxBudgeted = parentStructure?.level1IsMaxBudgeted ?? false;
            const level1IsUsingLaborBillingMethod = parentStructure?.level1IsUsingLaborBillingMethod ?? false;

            structure = this.estimateService.createStructure(
              this.currentEstimate.id,
              level1Node.data.id,
              newLevel2.id,
              '',
              level1IsMaxBudgeted,
              level2IsMaxBudgeted,
              level1IsUsingLaborBillingMethod,
              level2IsUsingLaborBillingMethod,
              level1IsLumpSum
            );
          }
          return this.estimateService.saveChanges();
        })
      )
      .subscribe(() => {
        // create new Level2 Node
        const newLevel2Node = this.createNode(
          newLevel2.code,
          newLevel2.description,
          newLevel2,
          this.level2,
          level1Node
        );
        // create Department Nodes and add to Level2 Node
        for (const departmentEstimate of this.departmentEstimates) {
          if (departmentEstimate.isAssociatedToAllStructures) {
            this.addDept(departmentEstimate, newLevel2Node);
          }
        }
        // add Level2 Node to Level1 Node
        level1Node.children.push(newLevel2Node);
        // expand parent and edit new Level2 Node
        const indexOf = this.expandedLevel1Ids.indexOf(level1Node.data.id);
        if (indexOf === -1) this.expandedLevel1Ids.push(level1Node.data.id);
        this.editSubLevel(newLevel2Node);
      });
  }

  private removeSubLevel() {
    if (!this.selectedNode) return;

    const node: TreeNode<Level1 | Level2> = this.selectedNode;

    if (node.type === this.level1) {
      const deleteLevel1StructuresObservable = this.estimateService
        .deleteLevel1Structures(this.currentEstimate.id, node.data.id)
        .pipe(
          map(structureEntities => {
            if (structureEntities) {
              return from(
                this.estimateService.detachEntities(structureEntities).then(() => {
                  const indexOf = this.level1Tree.indexOf(node);
                  if (indexOf !== -1) {
                    this.level1Tree.splice(indexOf, 1);
                  }
                })
              );
            }
            return EMPTY;
          })
        );

      if (this.estimateService.entityManagerHasChanges()) {
        this.estimateService
          .saveChanges()
          .pipe(switchMap(() => deleteLevel1StructuresObservable))
          .subscribe(); // delete Level1 from database
      } else {
        // delete Level1 from database
        deleteLevel1StructuresObservable.subscribe();
      }
    } else if (node.type === this.level2) {
      const deleteLevel2StructuresObservable = this.estimateService
        .findLevel2Structure(this.currentEstimate.id, node.parent.data.id, node.data.id)
        .pipe(
          switchMap(structure => {
            return this.estimateService.deleteLevel2Structure(structure.id);
          })
        )
        .pipe(
          map(structureEntities => {
            if (structureEntities) {
              from(
                this.estimateService.detachEntity(structureEntities[0]).then(() => {
                  // remove node from tree
                  const indexOf = node.parent.children.indexOf(node);
                  if (indexOf !== -1) {
                    node.parent.children.splice(indexOf, 1);
                  }
                })
              );
            }

            return EMPTY;
          })
        );

      if (this.estimateService.entityManagerHasChanges()) {
        this.estimateService
          .saveChanges()
          .pipe(
            switchMap(
              () =>
                // find associated Structure entity and make Structure.Level2 NULL
                deleteLevel2StructuresObservable
            )
          )
          .subscribe();
      } else {
        // find associated Structure entity and make Structure.Level2 NULL
        deleteLevel2StructuresObservable.subscribe();
      }
    }
  }

  private addDept(departmentEstimate: DepartmentEstimate, node: TreeNode<Level1 | Level2>) {
    const estimate = this.currentEstimate;

    const isLevel1 = estimate.structureDepth === StructureDepthEnum.Level1;
    const level1 = isLevel1 ? node.data : node.parent.data;
    const level2 = !isLevel1 ? node.data : node.parent.data;

    this.estimateService.findLevel2Structure(estimate.id, level1?.id, level2?.id).subscribe(results => {
      const structure = results;
      void this.estimateStructureLinksService.createEstimateLineBasedOnStructure(
        structure,
        estimate,
        departmentEstimate.id
      );
    });
  }

  public expandAll() {
    //pushes all level1s and level2s into
    this.isExpandHidden = false;
    for (const level1Node of this.level1Tree) {
      this.expandedLevel1Ids.push(level1Node.data.id);
      for (const level2Node of level1Node.children) {
        this.expandedLevel2Ids.push(level2Node.data.id);
      }
    }
  }

  public collapseAll() {
    //empties both arrays of expanded levels
    this.isExpandHidden = true;
    this.expandedLevel1Ids = <string[]>[];
    this.expandedLevel2Ids = <string[]>[];
  }

  public expandNode(node: TreeNode<Level1 | Level2>): void {
    if (node.type === this.level1) {
      this.expandedLevel1Ids.push(node.data.id);
    } else if (node.type === this.level2) {
      this.expandedLevel2Ids.push(node.data.id);
    }
  }

  public collapseNode(node: TreeNode<Level1 | Level2>): void {
    if (node.type === this.level1) {
      const indexOf = this.expandedLevel1Ids.indexOf(node.data.id);
      if (indexOf !== -1) this.expandedLevel1Ids.splice(indexOf, 1);
    } else if (node.type === this.level2) {
      const indexOf = this.expandedLevel2Ids.indexOf(node.data.id);
      if (indexOf !== -1) this.expandedLevel2Ids.splice(indexOf, 1);
    }
  }

  private expandRecursive(node: TreeNode<Level1 | Level2>, isExpand: boolean) {
    node.expanded = isExpand;
    if (node.children) {
      node.children.forEach(childNode => {
        this.expandRecursive(childNode, isExpand);
      });
    }
  }

  public openSnackBar(message: string, action: string) {
    const modalCopied = this.translate.instant('MODAL.COPIED') as string;
    this.snackBar.open(`${message} ${modalCopied}`, action, {
      duration: 3500
    });
  }
}
